import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import * as React from 'react';

import { CheckmarkLine, ChevronDown, Close, Search } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { Button } from '../lib/button.jsx';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '../lib/input-group.jsx';
import { typographyVariants } from './typography.jsx';

const Combobox = ComboboxPrimitive.Root;

function ComboboxValue({ ...props }: Readonly<ComboboxPrimitive.Value.Props>) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxTrigger({
  className,
  ...props
}: Readonly<ComboboxPrimitive.Trigger.Props>) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      tabIndex={0}
      className={cn(
        //focus state
        'focus-visible:outline-2 focus-visible:outline-action-focusBorder',
        className,
      )}
      render={
        <Button icon size="small" variant="text">
          <ChevronDown className="pointer-events-none size-4" />
        </Button>
      }
      {...props}
    />
  );
}

function ComboboxClear({
  className,
  disabled,
  ...props
}: Readonly<ComboboxPrimitive.Clear.Props>) {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      {...props}
      render={
        <InputGroupAddon
          align={'inline-end'}
          className={cn('hidden shrink-0 pr-1 data-visible:flex', className)}
        >
          <Button icon size="small" variant="text" disabled={disabled}>
            <Close className="pointer-events-none size-4" />
          </Button>
        </InputGroupAddon>
      }
    />
  );
}

function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  size = 'medium',
  color = 'default',
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size' | 'color' | 'className'> &
  React.ComponentProps<typeof InputGroup> & {
    showTrigger?: boolean;
    showClear?: boolean;
  }) {
  return (
    <ComboboxPrimitive.InputGroup
      render={<InputGroup className={className} size={size} color={color} />}
    >
      <ComboboxPrimitive.Input
        render={<InputGroupInput disabled={disabled} />}
        {...props}
      />
      {showClear && <ComboboxClear disabled={disabled} />}
      <InputGroupAddon
        data-hidden={props.readOnly || disabled}
        align="inline-end"
        className="data-[hidden=true]:hidden"
      >
        <ComboboxTrigger />
      </InputGroupAddon>
      {children}
    </ComboboxPrimitive.InputGroup>
  );
}
/**
 * A search field rendered inside `ComboboxContent`, above `ComboboxList`.
 *
 * Use it for the "select with search" pattern, where the anchor is a
 * `ComboboxSelectTrigger` showing the selected value instead of a text input.
 */
function ComboboxSearch({
  className,
  placeholder = 'Search',
  ...props
}: Readonly<ComboboxPrimitive.Input.Props>) {
  return (
    <div
      data-slot="combobox-search"
      className="flex h-9 shrink-0 items-center gap-2 border-b border-border-primary px-3"
    >
      <Search className="pointer-events-none size-4 shrink-0 fill-icon-secondary" />
      <ComboboxPrimitive.Input
        data-slot="combobox-search-input"
        placeholder={placeholder}
        className={cn(
          typographyVariants({
            variant: 'bodyShort01',
          }),
          'h-8 w-full min-w-0 bg-transparent text-text-primary outline-none placeholder:text-text-placeholder',
          className,
        )}
        {...props}
      />
    </div>
  );
}

/**
 * A trigger that renders the field itself (selected value + chevron), so the
 * combobox reads like a `Select`. Pair it with `ComboboxSearch` inside
 * `ComboboxContent` when the list needs filtering.
 */
function ComboboxSelectTrigger({
  className,
  children,
  placeholder,
  disabled = false,
  size = 'medium',
  color = 'default',
  showClear = false,
  ...props
}: Omit<ComboboxPrimitive.Trigger.Props, 'placeholder'> & {
  size?: 'small' | 'medium';
  color?: 'default' | 'warning' | 'error';
  placeholder?: React.ReactNode;
  showClear?: boolean;
}) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-select-trigger"
      data-size={size}
      data-color={color}
      disabled={disabled}
      className={cn(
        typographyVariants({
          variant: 'bodyShort01',
        }),
        'group/combobox-select-trigger inline-flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm border border-border-primary bg-ui-field02 px-3 text-text-primary data-[size=medium]:h-10 data-[size=small]:h-8',
        //focus state
        'hover:border-action-focusBorder focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-action-focusBorder',
        //placeholder state
        'data-placeholder:text-text-placeholder',
        //warning color variant
        'data-[color=warning]:border-2 data-[color=warning]:border-warning-main data-[color=warning]:focus-visible:outline-none',
        //error color variant
        'data-[color=error]:border-2 data-[color=error]:border-error-main data-[color=error]:focus-visible:outline-none',
        //disabled state
        'disabled:pointer-events-none disabled:border-border-disabled disabled:bg-action-disabledBackground disabled:text-text-placeholder',
        className,
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate text-left">
        {children ?? <ComboboxValue placeholder={placeholder} />}
      </span>
      {showClear && (
        <ComboboxClear
          disabled={disabled}
          className="order-0 pr-0"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        />
      )}
      <ChevronDown className="pointer-events-none size-4 shrink-0 fill-tertiary-main group-disabled/combobox-select-trigger:fill-action-disabled01" />
    </ComboboxPrimitive.Trigger>
  );
}

function ComboboxContent({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'
  >) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-popover"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            'group/combobox-content pointer-events-auto relative flex max-h-[min(--spacing(72),max(--spacing(20),var(--available-height)))] w-(--anchor-width) max-w-(--available-width) flex-col overflow-hidden rounded-sm border border-ui-tertiary bg-ui-primary text-text-primary shadow-[0px_2px_4px_0px_rgba(9,30,66,0.16)] drop-shadow-[0px_0px_1px_0px_rgba(9,30,66,0.24)] duration-100 outline-none data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxList({
  className,
  ...props
}: Readonly<ComboboxPrimitive.List.Props>) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        'no-scrollbar min-h-0 flex-auto scroll-py-1 overflow-y-auto overscroll-contain',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxItem({
  className,
  children,
  ...props
}: Readonly<ComboboxPrimitive.Item.Props>) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        typographyVariants({
          variant: 'bodyShort01',
        }),
        'group/combobox-item grid h-10 min-w-0 cursor-pointer grid-cols-[auto_minmax(0,1fr)] items-center gap-2 overflow-hidden px-3 py-1.5 text-ellipsis whitespace-nowrap text-text-primary *:cursor-pointer',
        //focus state styles
        'data-highlighted:bg-ui-hover data-highlighted:text-text-primary data-highlighted:data-selected:bg-ui-highlight',
        // selected state styles
        'has-data-[state=checked]:bg-background-tertiary aria-selected:bg-background-tertiary data-[state=checked]:fill-tertiary-main data-[state=checked]:text-tertiary-main',
        //hover state styles
        'hover:bg-ui-hover has-data-[state=checked]:hover:bg-ui-highlight data-[state=checked]:hover:bg-ui-highlight',
        //disabled state styles
        'aria-disabled:pointer-events-none aria-disabled:text-action-disabled01 aria-disabled:hover:bg-ui-primary aria-disabled:data-highlighted:bg-ui-primary aria-disabled:data-highlighted:text-action-disabled01',
        className,
      )}
      {...props}
    >
      {children}
    </ComboboxPrimitive.Item>
  );
}

function ComboboxItemIndicator({
  className,
  children,
  ...props
}: Readonly<ComboboxPrimitive.ItemIndicator.Props>) {
  return (
    <ComboboxPrimitive.ItemIndicator
      data-slot="combobox-item-indicator"
      className={cn(
        'flex size-4 shrink-0 items-center justify-center',
        className,
      )}
      {...props}
    >
      {children ?? <CheckmarkLine className="size-4 fill-tertiary-main" />}
    </ComboboxPrimitive.ItemIndicator>
  );
}

/**
 * A checkbox driven by the surrounding `ComboboxItem`'s own selected state, for
 * multiple selection. It is presentational only — the item handles the click,
 * so no interactive element is nested inside the option.
 */
function ComboboxItemCheckbox({
  className,
  ...props
}: Readonly<React.ComponentProps<'span'>>) {
  return (
    <span
      data-slot="combobox-item-checkbox"
      aria-hidden
      className={cn(
        'flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border border-icon-primary',
        //selected state
        'group-data-selected/combobox-item:border-transparent group-data-selected/combobox-item:bg-tertiary-main',
        //disabled state
        'group-data-disabled/combobox-item:border-action-disabled01',
        //selected + disabled state
        '[[data-slot=combobox-item][data-disabled][data-selected]_&]:border-transparent [[data-slot=combobox-item][data-disabled][data-selected]_&]:bg-action-disabled01',
        className,
      )}
      {...props}
    >
      <ComboboxPrimitive.ItemIndicator
        data-slot="combobox-item-indicator"
        className="flex size-4 items-center justify-center"
      >
        <CheckmarkLine className="size-4 fill-greyscale-white p-1" />
      </ComboboxPrimitive.ItemIndicator>
    </span>
  );
}

/**
 * A row above the list for actions such as "Select all" / "Clear all" in a
 * multiple-selection combobox.
 */
function ComboboxActions({
  className,
  ...props
}: Readonly<React.ComponentProps<'div'>>) {
  return (
    <div
      data-slot="combobox-actions"
      className={cn(
        'flex shrink-0 items-center gap-2 border-none px-3 py-2',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxGroup({
  className,
  ...props
}: Readonly<ComboboxPrimitive.Group.Props>) {
  return (
    <ComboboxPrimitive.Group
      data-slot="combobox-group"
      className={cn(className)}
      {...props}
    />
  );
}

function ComboboxLabel({
  className,
  ...props
}: Readonly<ComboboxPrimitive.GroupLabel.Props>) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn(
        typographyVariants({
          variant: 'overline1',
          color: 'secondary',
        }),
        'sticky top-0 bg-ui-primary px-3 pt-4 pb-1 wrap-break-word',
      )}
      {...props}
    />
  );
}

function ComboboxCollection({
  ...props
}: Readonly<ComboboxPrimitive.Collection.Props>) {
  return <ComboboxPrimitive.Collection {...props} />;
}

function ComboboxEmpty({
  className,
  ...props
}: Readonly<ComboboxPrimitive.Empty.Props>) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        typographyVariants({
          variant: 'bodyShort01',
          color: 'secondary',
        }),
        'hidden justify-center p-4 group-data-empty/combobox-content:flex',
        className,
      )}
      {...props}
    />
  );
}

function ComboboxSeparator({
  className,
  ...props
}: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn('bg-border -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function ComboboxChips({
  className,
  showClear = true,
  disabled,
  size = 'medium',
  color = 'default',
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
  ComboboxPrimitive.Chips.Props & {
    color?: 'default' | 'warning' | 'error';
    size?: 'small' | 'medium';
    showClear?: boolean;
    disabled?: boolean;
  }) {
  return (
    <ComboboxPrimitive.InputGroup
      data-size={size}
      data-state={color}
      className={cn(
        'group/input-group inline-flex w-full items-center overflow-hidden rounded-sm bg-ui-field02 shadow-none placeholder:text-text-placeholder data-[size=medium]:min-h-10 data-[size=small]:min-h-8',
        //default
        'border-border-primary hover:border-action-focusBorder data-[state=default]:border',
        'has-[[data-slot=combobox-chip-input]:focus-visible]:outline-2 has-[[data-slot=combobox-chip-input]:focus-visible]:outline-action-focusBorder',
        // warning
        'data-[state=warning]:border-2 data-[state=warning]:border-warning-main data-[state=warning]:focus-visible:border-warning-main data-[state=warning]:has-[[data-slot=combobox-chip-input]:focus-visible]:outline-none',
        //error
        'data-[state=error]:border-2 data-[state=error]:border-error-main data-[state=error]:focus-visible:border-error-main data-[state=error]:has-[[data-slot=combobox-chip-input]:focus-visible]:outline-none',
        className,
      )}
    >
      <ComboboxPrimitive.Chips
        data-slot="combobox-chips"
        className={cn(
          'flex min-w-0 flex-1 flex-wrap items-center gap-1 py-1 pl-2',
        )}
        {...props}
      >
        {props.children}
      </ComboboxPrimitive.Chips>
      {showClear && <ComboboxClear disabled={disabled} />}
      <InputGroupAddon align="inline-end" className="shrink-0">
        <ComboboxTrigger />
      </InputGroupAddon>
    </ComboboxPrimitive.InputGroup>
  );
}

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        'bg-background-silver',
        'has-data-[slot=combobox-chip-remove]:pr-0',
        'box-border flex h-6 w-fit max-w-full min-w-0 shrink-0 items-center gap-1 overflow-hidden rounded-full px-2 py-1 select-none aria-disabled:pointer-events-none aria-disabled:opacity-[0.32]',
        typographyVariants({
          variant: 'caption1',
          className: 'text-silver-contrastText',
        }),
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          className="mr-1 size-4 shrink-0 opacity-70"
          data-slot="combobox-chip-remove"
          render={
            <button type="button" className="cursor-pointer">
              <Close className="pointer-events-none size-4 fill-silver-contrastText" />
            </button>
          }
        />
      )}
    </ComboboxPrimitive.Chip>
  );
}

function ComboboxChipsInput({
  className,
  ...props
}: Readonly<ComboboxPrimitive.Input.Props>) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn(
        'min-w-16 flex-1 py-1 outline-none placeholder:text-text-placeholder',
        typographyVariants({
          variant: 'bodyShort01',
        }),
        className,
      )}
      {...props}
    />
  );
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxItemCheckbox,
  ComboboxActions,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  ComboboxSearch,
  ComboboxSelectTrigger,
  ComboboxCollection,
};
